/**
 * Instrumentation helpers (P2 design §4.8): the easy way to tag controls and surfaces and to
 * bracket user operations, typed against the registries so a typo is a type error.
 *
 *   <button {...ui("chat.send")}>          <li {...ui("forecast-manager.row", f.id)}>
 *   <section {...surface("report.section", slug)}>
 *   const intent = beginIntent("forecast.delete", { forecast_id }); ... intent.end("ok");
 *
 * The registries are under `./registry/`; the guide is
 * `docs/capsules/telemetry-instrumentation.md`.
 */
import { intentEmitter } from "./intentEmitter.js";
import { INTENTS } from "./registry";
import type { IntentName, IntentOutcome, SurfaceName, UiId } from "./registry";

export type { IntentName, IntentOutcome, SurfaceName, UiId };

/** A `data-ui-key` / `data-surface-key` value: a stable object id, never free text. */
export type InstrumentKey = string | number | null | undefined;

export interface UiAttrs {
  "data-ui": UiId;
  "data-ui-key"?: string;
}

export interface SurfaceAttrs {
  "data-surface": SurfaceName;
  "data-surface-key"?: string;
}

const hasKey = (key: InstrumentKey): key is string | number =>
  key !== null && key !== undefined && key !== "";

/** Attributes for an interactive element: `{...ui("chat.send")}`, `{...ui("forecast-manager.row", id)}`. */
export function ui(id: UiId, key?: InstrumentKey): UiAttrs {
  return hasKey(key) ? { "data-ui": id, "data-ui-key": String(key) } : { "data-ui": id };
}

/**
 * Attributes for the element a hover-revealed control (`revealHover` in the registry) appears
 * over: `{...hoverAnchor("timeline.forecast-mark", iso)}`. Not a control itself; replays move the
 * pointer to it to reveal `id` with this `key`.
 */
export function hoverAnchor(id: UiId, key?: InstrumentKey): Record<string, string> {
  return hasKey(key)
    ? { "data-ui-hover": id, "data-ui-hover-key": String(key) }
    : { "data-ui-hover": id };
}

/** Attributes for a surface's root element: `{...surface("report", forecastId)}`. */
export function surface(name: SurfaceName, key?: InstrumentKey): SurfaceAttrs {
  return hasKey(key)
    ? { "data-surface": name, "data-surface-key": String(key) }
    : { "data-surface": name };
}

/**
 * The controls the shared primitives carry: `ModalShell`'s backdrop (`dismiss`) and panel
 * (`dialog`), `CloseButton`
 * (`close`), `DrawerHelpToggle` / `DrawerHelp` / `HelpReopenButton` (`help-dismiss`,
 * `help-reopen`).
 */
export type PrimitiveControl = "dismiss" | "dialog" | "close" | "help-dismiss" | "help-reopen";

/**
 * The `data-ui` id a shared primitive derives from its enclosing surface, e.g. `settings` →
 * `settings.close`; undefined outside a tagged surface. Each derived id is registered in
 * `registry/annotations.json` (the registry generation fails otherwise).
 */
export function primitiveUiId(
  surfaceName: string | null | undefined,
  control: PrimitiveControl,
): string | undefined {
  return surfaceName ? `${surfaceName}.${control}` : undefined;
}

/** The value a param of registry type `T` takes (an enum is its union of values). */
type IntentParamValue<T> = T extends "str"
  ? string
  : T extends "int" | "num"
    ? number
    : T extends "bool"
      ? boolean
      : T extends "strs"
        ? readonly string[]
        : T extends readonly (infer V)[]
          ? V
          : never;

/**
 * Params of intent `N`, typed from its registry schema: only its registered keys, each with
 * its registered type. Every param is optional (params are additive, design §4.6).
 */
export type IntentParams<N extends IntentName = IntentName> = {
  readonly [K in keyof (typeof INTENTS)[N]["params"]]?: IntentParamValue<
    (typeof INTENTS)[N]["params"][K]
  > | null;
};

export interface BeginIntentOptions {
  /** The triggering control's `data-ui` id, or chat / shortcut / tour / deeplink / auto. */
  origin?: UiId | "chat" | "shortcut" | "tour" | "deeplink" | "auto";
  /** The surface it was issued from. */
  surface?: SurfaceName;
  /** A caller-supplied id (e.g. `chat.ask` reuses the chat turn id). */
  id?: string;
}

type IntentScopeOf<N extends IntentName> = (typeof INTENTS)[N]["scope"];

/** The milestone names intent `N` registers (`first_frame_ms`, `result_ms`, …). */
export type IntentMilestoneOf<N extends IntentName> = (typeof INTENTS)[N]["milestones"][number];

/** Outcomes intent `N` can end with: `abandoned` only for user-side (`ui`) intents. */
export type IntentOutcomeOf<N extends IntentName> = N extends IntentName
  ? IntentScopeOf<N> extends "ui"
    ? IntentOutcome
    : Exclude<IntentOutcome, "abandoned">
  : never;

interface IntentHandleBase {
  /** The intent's id (`intent_id`); empty while the intent group is off. */
  readonly id: string;
}

/**
 * A handle to intent `N`; a `point` intent has no outcome or milestones. Distributes over a
 * union of names, so a handle that may be a point intent's must be narrowed before `end`.
 */
export type IntentHandle<N extends IntentName = IntentName> = N extends IntentName
  ? IntentScopeOf<N> extends "point"
    ? IntentHandleBase
    : IntentHandleBase & {
        /** Record the outcome; idempotent. */
        end(
          outcome: IntentOutcomeOf<N>,
          milestones?: Partial<Record<IntentMilestoneOf<N>, number>>,
        ): void;
        /** Record a registered milestone reached so far. */
        mark(milestone: IntentMilestoneOf<N>): void;
        /**
         * Long-running intents: the client run id whose outcome ends the intent
         * (services/intentRuns.js). A no-op on other handles' runs that never end.
         */
        attachRun(runId: string | null | undefined): void;
        /** Long-running intents: the run's trace id, so any tab can end the intent. */
        bindTrace(traceId: string | null | undefined, opts?: { runId?: string | null }): void;
      }
  : never;

type IntentBeginListener = (name: IntentName, params?: Readonly<Record<string, unknown>>) => void;
const beginListeners = new Set<IntentBeginListener>();

/**
 * Call `listener` with each intent's name and params as it begins, whether or not the intent
 * group is on. In-page only: nothing is recorded or sent. Returns the unsubscribe.
 */
export function onIntentBegin(listener: IntentBeginListener): () => void {
  beginListeners.add(listener);
  return () => {
    beginListeners.delete(listener);
  };
}

/**
 * Start an intent and return its handle (lib/telemetry/intentEmitter.js). While the intent
 * group is off the handle is inert: its `id` is empty and its methods do nothing.
 */
export function beginIntent<N extends IntentName>(
  name: N,
  params?: IntentParams<N>,
  opts?: BeginIntentOptions,
): IntentHandle<N> {
  for (const listener of beginListeners) {
    try {
      listener(name, params as Readonly<Record<string, unknown>> | undefined);
    } catch {
      // A listener must never break the operation it observes.
    }
  }
  return intentEmitter().begin(name, params, opts) as unknown as IntentHandle<N>;
}

type AnyEndable = {
  end?: (outcome: IntentOutcome) => void;
};

/**
 * Run `fn` as the intent `name`: the intent starts before it and ends with its outcome —
 * `ok` when `fn` returns (or its promise resolves), `error` when it throws (or rejects),
 * unless `fn` ended it first. A long-running intent is left to its run on success: its
 * outcome follows the run (design §4.6), and only a failure before the run ends it here.
 */
export function withIntent<N extends IntentName, T>(
  name: N,
  fn: (intent: IntentHandle<N>) => T,
  params?: IntentParams<N>,
  opts?: BeginIntentOptions,
): T {
  const intent = beginIntent(name, params, opts);
  const h = intent as unknown as AnyEndable;
  const longRunning = intentEmitterScope(name) === "long-running";
  const done = () => {
    if (!longRunning) h.end?.("ok");
  };
  const failed = () => h.end?.("error");
  let out: T;
  try {
    out = fn(intent);
  } catch (e) {
    failed();
    throw e;
  }
  if (out && typeof (out as { then?: unknown }).then === "function") {
    (out as unknown as Promise<unknown>).then(done, failed);
  } else done();
  return out;
}

function intentEmitterScope(name: IntentName): string {
  return INTENTS[name]?.scope ?? "ui";
}
